import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Sparkles, Loader2,
  Lock, Mountain,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import PasswordInput from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import useEffectivePricing, { formatPrice } from "@/lib/useEffectivePricing";
import { useLanguage } from "@/context/LanguageContext";
import usePromoStatus from "@/lib/usePromoStatus";

/**
 * OnboardingQuiz — the pre-signup gate.
 *
 * Flow:
 *   1. On mount, POST /onboarding/start → get questions + signed challenge.
 *   2. User answers 5 questions; a progress bar tracks the walk-through.
 *   3. On the last answer, POST /onboarding/complete → get onboarding_token
 *      + archetype + starting chapter, revealing a personalized reveal card.
 *   4. User continues to the register form (still same page); submits
 *      email + name + password + the onboarding_token to /auth/register.
 *
 * Anti-spam mechanics complement the backend gate:
 *   - Multi-step render flow (bots typically only fire the final POST)
 *   - Hidden `website` honeypot on the register form
 *   - Client waits min-elapsed-seconds before enabling the "continue" flow
 */
export default function OnboardingQuiz() {
  const { t, i18n } = useTranslation();
  const { params: P } = usePromoStatus();
  const { lang: preferredLang, setLang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || null;
  // Tier intent — set either when a visitor clicks a Pricing card (URL param)
  // or when they pick a tier on the quiz-reveal screen (setIntentTier). It
  // persists in localStorage so it survives the reveal → register hop, and
  // <PendingUpgradeBanner> on the dashboard picks it up to resume-to-checkout.
  const initialIntent = (() => {
    const raw = searchParams.get("intent");
    if (raw === "basic" || raw === "premium") return raw;
    try {
      const stored = localStorage.getItem("summit_intent_tier");
      if (stored === "basic" || stored === "premium") return stored;
    } catch { /* ignore */ }
    return null;
  })();
  const [intentTier, setIntentTier] = useState(initialIntent);
  const pricing = useEffectivePricing();
  // Persist / clear localStorage in sync with state changes.
  useEffect(() => {
    try {
      if (intentTier) localStorage.setItem("summit_intent_tier", intentTier);
      else localStorage.removeItem("summit_intent_tier");
    } catch { /* ignore */ }
  }, [intentTier]);
  const { setUser } = useAuth();

  const [challenge, setChallenge] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [minSecs, setMinSecs] = useState(5);
  const [startedAt, setStartedAt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [completing, setCompleting] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [starting, setStarting] = useState(true);

  // Mirror the preferred language into the older 'summit_preferred_lang'
  // key so the /auth/register payload stays consistent for older backends.
  useEffect(() => {
    try { localStorage.setItem("summit_preferred_lang", preferredLang); } catch { /* ignore */ }
  }, [preferredLang]);

  // Register form state
  const [form, setForm] = useState({ name: "", email: "", password: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [riskAccepted, setRiskAccepted] = useState(false);

  // ── Start challenge on mount ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    api.post("/onboarding/start").then((r) => {
      if (cancelled) return;
      setChallenge(r.data.challenge);
      setQuestions(r.data.questions || []);
      setMinSecs(r.data.min_elapsed_seconds || 5);
      setStartedAt(Date.now());
      setStarting(false);
    }).catch((e) => {
      if (cancelled) return;
      const detail = e.response?.data?.detail || t("onboarding.toasts.couldntStart");
      toast.error(detail);
      setStarting(false);
    });
    return () => { cancelled = true; };
  }, [t]);

  const totalSteps = questions.length;
  const currentQ = questions[step];
  const progress = totalSteps ? Math.round(((step + (reveal ? 1 : 0)) / (totalSteps + 1)) * 100) : 0;

  const chooseAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    // Auto-advance for smoother flow
    if (step < totalSteps - 1) {
      setTimeout(() => setStep((s) => s + 1), 200);
    }
  };

  const canSubmitQuiz = useMemo(() => {
    if (!totalSteps) return false;
    return questions.every((q) => answers[q.id]);
  }, [questions, answers, totalSteps]);

  const submitQuiz = async () => {
    if (!challenge || !canSubmitQuiz) return;
    // Enforce the server-side minimum on the client too — cheap UX polish
    const elapsed = (Date.now() - (startedAt || 0)) / 1000;
    if (elapsed < minSecs) {
      toast.info(t("onboarding.toasts.beat", { seconds: Math.ceil(minSecs - elapsed) }));
      return;
    }
    setCompleting(true);
    try {
      const r = await api.post("/onboarding/complete", {
        challenge,
        answers,
        website: form.website, // honeypot passthrough
        lang: (i18n.language || "en").slice(0, 2).toLowerCase(),
      });
      setReveal(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("onboarding.toasts.couldntFinish"));
    } finally {
      setCompleting(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (!reveal?.onboarding_token) {
      toast.error(t("onboarding.toasts.completeFirst"));
      return;
    }
    if (form.website) return; // honeypot tripped
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error(t("onboarding.toasts.fillFields"));
      return;
    }
    if (!riskAccepted) {
      toast.error(t("onboarding.register.riskRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/auth/register", {
        risk_accepted: true,
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        preferred_language: preferredLang,
        ref: refCode || undefined,
        onboarding_token: reveal.onboarding_token,
      });
      setUser(r.data.user);
      toast.success(t("onboarding.toasts.welcome"));
      const dest = intentTier
        ? `/dashboard?welcome=1&intent=${intentTier}`
        : "/dashboard?welcome=1";
      navigate(dest, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "onboarding_expired") {
        toast.error(t("onboarding.toasts.expired"));
        setTimeout(() => window.location.reload(), 800);
      } else if (detail === "onboarding_invalid" || detail === "onboarding_required") {
        toast.error(t("onboarding.toasts.retake"));
        setReveal(null); setStep(0); setAnswers({});
      } else {
        toast.error(typeof detail === "string" ? detail : t("onboarding.toasts.registerFailed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading state ────────────────────────────────────────────────────
  if (starting) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <Loader2 size={22} className="animate-spin text-amber-400" />
      </div>
    );
  }
  if (!questions.length) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center gap-4">
        <p className="text-slate-300">{t("onboarding.loadFail")}</p>
        <Link to="/" className="text-amber-400 hover:text-amber-300 text-sm font-semibold">
          {t("onboarding.backHome")}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden" data-testid="onboarding-quiz-root">
      {/* Ambient orbs */}
      <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_20%_30%,rgba(30,64,175,0.55),transparent_55%),radial-gradient(circle_at_80%_70%,rgba(245,158,11,0.4),transparent_50%)] pointer-events-none" />

      <div className="relative max-w-2xl mx-auto px-6 py-16 lg:py-20">
        {/* Brand + language picker */}
        <div className="mb-10 flex items-center justify-between">
          <Link to="/" className="text-xs uppercase tracking-[0.3em] text-amber-400 font-bold">
            Peak Market Society
          </Link>
          <div className="flex items-center gap-4">
            <div
              className="flex items-center gap-1 rounded-full bg-white/5 border border-white/10 p-1"
              data-testid="onboarding-language-picker"
              role="tablist"
              aria-label="Choose language"
              translate="no"
            >
              {[
                { code: "en", label: "EN" },
                { code: "es", label: "ES" },
                { code: "fr", label: "FR" },
              ].map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setLang(l.code)}
                  data-testid={`onboarding-language-${l.code}`}
                  role="tab"
                  aria-selected={preferredLang === l.code}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors ${
                    preferredLang === l.code
                      ? "bg-amber-500 text-slate-950"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <Link to="/login" className="text-xs text-slate-400 hover:text-white transition-colors">
              {t("onboarding.loginLink")} <span className="font-semibold underline underline-offset-2">{t("onboarding.loginLinkAction")}</span>
            </Link>
          </div>
        </div>

        {/* Progress rail */}
        <div className="mb-8">
          {intentTier && !reveal && (
            <div
              data-testid="onboarding-intent-pill"
              className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] font-bold text-amber-200"
            >
              <Sparkles size={11} />
              {t("onboarding.intentPill", { tier: intentTier === "basic" ? t("tier.basic") : t("tier.premium") })}
            </div>
          )}
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold mb-2">
            <span>
              {reveal
                ? t("onboarding.progress.pathUnlocked")
                : t("onboarding.progress.questionOf", { current: Math.min(step + 1, totalSteps), total: totalSteps })}
            </span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* ─── QUIZ STEPS ────────────────────────────────────────── */}
        {!reveal && currentQ && (
          <div data-testid="onboarding-quiz-step" className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight text-white">
              {t(`onboarding.questions.${currentQ.id}.prompt`, currentQ.prompt)}
            </h1>
            {currentQ.help && (
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                {t(`onboarding.questions.${currentQ.id}.help`, currentQ.help)}
              </p>
            )}
            <div className="mt-8 space-y-2.5">
              {currentQ.options.map((opt) => {
                const selected = answers[currentQ.id] === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => chooseAnswer(currentQ.id, opt.id)}
                    data-testid={`onboarding-option-${currentQ.id}-${opt.id}`}
                    className={`w-full text-left px-5 py-4 rounded-xl border transition-all ${
                      selected
                        ? "bg-amber-500/15 border-amber-400 text-white"
                        : "bg-white/5 border-white/10 text-slate-200 hover:bg-white/10 hover:border-white/20"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        selected ? "border-amber-400 bg-amber-400" : "border-white/30"
                      }`}>
                        {selected && <CheckCircle2 size={14} className="text-slate-900" />}
                      </div>
                      <span className="text-sm sm:text-base font-medium">
                        {t(`onboarding.questions.${currentQ.id}.options.${opt.id}`, opt.label)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex items-center justify-between gap-3">
              <Button
                variant="ghost"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-30"
                data-testid="onboarding-back-button"
              >
                <ArrowLeft size={14} className="mr-1.5" /> {t("onboarding.buttons.back")}
              </Button>

              {step < totalSteps - 1 ? (
                <Button
                  onClick={() => setStep((s) => Math.min(totalSteps - 1, s + 1))}
                  disabled={!answers[currentQ.id]}
                  className="bg-white text-slate-950 hover:bg-slate-100 disabled:opacity-40 h-11 px-6 font-semibold"
                  data-testid="onboarding-next-button"
                >
                  {t("onboarding.buttons.next")} <ArrowRight size={14} className="ml-1.5" />
                </Button>
              ) : (
                <Button
                  onClick={submitQuiz}
                  disabled={!canSubmitQuiz || completing}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-950 h-11 px-6 font-semibold"
                  data-testid="onboarding-submit-button"
                >
                  {completing
                    ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> {t("onboarding.buttons.readingAnswers")}</>
                    : <><Sparkles size={14} className="mr-1.5" /> {t("onboarding.buttons.seeMyPath")}</>}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ─── REVEAL + REGISTER ─────────────────────────────────── */}
        {reveal && (
          <div data-testid="onboarding-reveal" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
            {/* Reveal card */}
            <div className="relative overflow-hidden rounded-2xl border border-amber-500/25 bg-gradient-to-br from-blue-950 via-slate-950 to-slate-950 p-8 mb-8">
              <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-amber-500/20 blur-3xl" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 border border-amber-500/30 px-3 py-1 text-[10px] uppercase tracking-[0.28em] text-amber-300 font-bold">
                  <Sparkles size={11} /> {t("onboarding.reveal.professorSays")}
                </div>
                <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-slate-400 font-semibold">
                  {t("onboarding.reveal.youAreA")}
                </div>
                <h2
                  className="mt-1 font-display font-black text-4xl sm:text-5xl text-white tracking-tighter leading-[1.05]"
                  data-testid="onboarding-archetype"
                >
                  {reveal.archetype}
                </h2>
                <p className="mt-4 text-slate-300 leading-relaxed">
                  {reveal.blurb}
                </p>
                {reveal.reflection && (
                  <div className="mt-5 rounded-lg bg-amber-500/10 border border-amber-500/30 p-4">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 font-semibold mb-1.5">
                      {t("onboarding.reveal.noteFromProfessor")}
                    </div>
                    <p
                      className="text-slate-100 leading-relaxed text-[15px] italic"
                      data-testid="onboarding-reflection"
                    >
                      &ldquo;{reveal.reflection}&rdquo;
                    </p>
                  </div>
                )}
                <div className="mt-6 rounded-lg bg-white/5 border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 font-semibold mb-4">
                    {t("onboarding.reveal.sampleRoadmap")}
                  </div>
                  <ol
                    data-testid="onboarding-roadmap"
                    className="relative border-l-2 border-amber-500/30 ml-2 pl-6 space-y-5"
                  >
                    {(reveal.roadmap || []).map((s, i) => (
                      <li key={i} className="relative">
                        <span className="absolute -left-[33px] top-1 flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-slate-950 font-mono font-black text-[11px]">
                          {i + 1}
                        </span>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold">
                          {s.when}
                        </div>
                        <div className="mt-0.5 text-sm sm:text-base font-semibold text-white leading-snug">
                          {s.title}
                        </div>
                        <div className="mt-1 text-xs text-slate-400 leading-relaxed">
                          {s.body}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>

            {/* Tier picker · fast-path for "I already know I want to pay" */}
            <div
              data-testid="onboarding-tier-picker"
              className="mb-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5"
            >
              <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 font-bold mb-1">
                {t("onboarding.tierPicker.heading")}
              </div>
              <p className="text-sm text-slate-400 mb-4 leading-relaxed">
                {t("onboarding.tierPicker.subtitle")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  {
                    id: "free",     value: null,
                    label: t("onboarding.tierPicker.labelFree"),
                    priceLabel: t("onboarding.tierPicker.priceFree"),
                    baseLabel: null,
                    note: t("onboarding.tierPicker.noteFree"),
                  },
                  {
                    id: "basic",    value: "basic",
                    label: t("onboarding.tierPicker.labelBasic"),
                    priceLabel: pricing.basic ? formatPrice(pricing.basic.effective) : "$750",
                    baseLabel: pricing.basic?.discounted ? formatPrice(pricing.basic.base) : null,
                    note: pricing.basic?.promoStage === "free"
                      ? t("onboarding.tierPicker.noteBasicFree", P)
                      : pricing.basic?.promoStage === "half"
                        ? t("onboarding.tierPicker.noteBasicHalf")
                        : t("onboarding.tierPicker.noteBasicFull"),
                  },
                  {
                    id: "premium",  value: "premium",
                    label: t("onboarding.tierPicker.labelPremium"),
                    priceLabel: pricing.premium ? formatPrice(pricing.premium.effective) : "$1,250",
                    baseLabel: pricing.premium?.discounted ? formatPrice(pricing.premium.base) : null,
                    note: t("onboarding.tierPicker.notePremium"),
                    highlight: true,
                  },
                ].map((c) => {
                  const selected = intentTier === c.value;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setIntentTier(c.value)}
                      data-testid={`onboarding-tier-${c.id}`}
                      aria-pressed={selected}
                      className={`relative text-left rounded-xl border-2 p-4 transition-all ${
                        selected
                          ? "border-amber-400 bg-amber-500/10 shadow-[0_10px_30px_-10px_rgba(245,158,11,0.35)]"
                          : "border-white/10 bg-white/[0.03] hover:border-white/25"
                      }`}
                    >
                      {c.highlight && !selected && (
                        <div className="absolute -top-2 right-3 text-[9px] uppercase tracking-widest font-bold bg-amber-500 text-slate-950 px-2 py-0.5 rounded-full">
                          {t("onboarding.tierPicker.popular")}
                        </div>
                      )}
                      <div className="flex items-baseline justify-between gap-2">
                        <div className={`font-display font-bold text-base ${selected ? "text-amber-200" : "text-white"}`}>
                          {c.label}
                        </div>
                        <div className="flex items-baseline gap-1.5">
                          {c.baseLabel && (
                            <div className="font-mono text-[11px] text-slate-500 line-through">{c.baseLabel}</div>
                          )}
                          <div className={`font-mono font-bold text-sm ${selected ? "text-amber-300" : "text-slate-400"}`}>
                            {c.priceLabel}
                          </div>
                        </div>
                      </div>
                      <div className={`mt-1 text-[11px] leading-snug ${selected ? "text-amber-100/80" : "text-slate-400"}`}>
                        {c.note}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Language emphasis picker · sits directly under the tier picker
                because language selection controls FAR more than UI copy —
                it gates Discord role assignment, translated trade setups,
                translated content channels, and the Professor's language. */}
            <div
              data-testid="onboarding-language-emphasis"
              className="mb-6 rounded-2xl border border-amber-400/20 bg-gradient-to-br from-amber-500/[0.07] via-white/[0.03] to-transparent backdrop-blur p-5"
              translate="no"
            >
              <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 font-bold mb-3">
                {t("onboarding.languagePicker.heading", "Pick your language")}
              </div>

              <div className="grid grid-cols-3 gap-2 mb-4">
                {[
                  { code: "en", label: "English", flag: "🇬🇧" },
                  { code: "es", label: "Español", flag: "🇪🇸" },
                  { code: "fr", label: "Français", flag: "🇫🇷" },
                ].map((l) => {
                  const selected = preferredLang === l.code;
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => setLang(l.code)}
                      data-testid={`onboarding-language-emphasis-${l.code}`}
                      aria-pressed={selected}
                      className={`relative rounded-xl border-2 p-3 transition-all text-center ${
                        selected
                          ? "border-amber-400 bg-amber-500/10 shadow-[0_10px_30px_-10px_rgba(245,158,11,0.35)]"
                          : "border-white/10 bg-white/[0.03] hover:border-white/25"
                      }`}
                    >
                      <div className="text-2xl leading-none mb-1">{l.flag}</div>
                      <div className={`font-display font-bold text-sm ${selected ? "text-amber-200" : "text-white"}`}>
                        {l.label}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Trilingual explanation — visible always in all three languages
                  so every visitor understands the weight of this choice
                  without needing to first switch. */}
              <div className="space-y-2 text-[12px] leading-relaxed">
                <p className={preferredLang === "en" ? "text-amber-100" : "text-slate-400"}>
                  <span className="font-mono text-[10px] uppercase tracking-widest mr-2 opacity-70">EN</span>
                  Your language choice controls everything: which Discord channels you see, the language of every trade setup, and how The Professor speaks to you. You can change it later in Account Settings, but it's the fastest way to make the whole platform feel like home.
                </p>
                <p className={preferredLang === "es" ? "text-amber-100" : "text-slate-400"}>
                  <span className="font-mono text-[10px] uppercase tracking-widest mr-2 opacity-70">ES</span>
                  Tu idioma controla todo: los canales de Discord que ves, el idioma de cada setup, y cómo te habla El Profesor. Puedes cambiarlo después en tu cuenta, pero es la forma más rápida de que toda la plataforma se sienta como en casa.
                </p>
                <p className={preferredLang === "fr" ? "text-amber-100" : "text-slate-400"}>
                  <span className="font-mono text-[10px] uppercase tracking-widest mr-2 opacity-70">FR</span>
                  Votre choix de langue contrôle tout : les canaux Discord que vous voyez, la langue de chaque setup, et la manière dont Le Professeur vous parle. Vous pouvez le modifier plus tard dans votre compte, mais c'est le moyen le plus rapide de rendre la plateforme vraiment à vous.
                </p>
              </div>
            </div>

            {/* Register form */}
            <form onSubmit={submitRegister} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-[10px] uppercase tracking-[0.28em] text-white font-bold mb-4">
                <Lock size={11} /> {t("onboarding.register.eyebrow")}
              </div>
              <h3 className="font-display font-bold text-2xl text-white leading-tight tracking-tight">
                {t("onboarding.register.title")}
              </h3>
              <p className="mt-2 text-sm text-slate-400">
                {t("onboarding.register.subtitle", { track: reveal.starting_chapter.split("·")[0].trim() })}
              </p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mb-1.5 block">{t("onboarding.register.fullName")}</label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="onboarding-name-input"
                    autoComplete="name"
                    required
                    className="h-11 bg-white/10 border-white/20 text-white placeholder:text-slate-500 focus-visible:ring-amber-400"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mb-1.5 block">{t("onboarding.register.email")}</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="onboarding-email-input"
                    autoComplete="email"
                    required
                    className="h-11 bg-white/10 border-white/20 text-white placeholder:text-slate-500 focus-visible:ring-amber-400"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mb-1.5 block">{t("onboarding.register.password")}</label>
                  <PasswordInput
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="onboarding-password-input"
                    autoComplete="new-password"
                    minLength={6}
                    required
                    className="h-11 bg-white/10 border-white/20 text-white placeholder:text-slate-500 focus-visible:ring-amber-400"
                  />
                </div>
              </div>

              {/* Honeypot — off-screen, not focusable */}
              <div style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
                <label>Website (leave blank)</label>
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </div>

              <label data-testid="onboarding-risk-ack" className="mt-6 flex items-start gap-3 rounded-xl border border-white/15 bg-white/[0.04] p-4 cursor-pointer">
                <Checkbox
                  checked={riskAccepted}
                  onCheckedChange={(v) => setRiskAccepted(v === true)}
                  data-testid="onboarding-risk-checkbox"
                  className="mt-0.5 border-amber-400 data-[state=checked]:bg-amber-500 data-[state=checked]:text-slate-950"
                />
                <span className="text-[12px] leading-relaxed text-slate-300">{t("onboarding.register.riskCheck")}</span>
              </label>

              <Button
                type="submit"
                disabled={submitting || !riskAccepted}
                className="w-full mt-4 h-auto min-h-12 py-3 whitespace-normal leading-snug text-center bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                data-testid="onboarding-register-submit"
              >
                {submitting ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" /> {t("onboarding.register.submitting")}</>
                ) : intentTier === "premium" ? (
                  <><Mountain size={15} className="mr-2 shrink-0" /> {t("onboarding.register.submitPremium")}</>
                ) : intentTier === "basic" ? (
                  <><Mountain size={15} className="mr-2 shrink-0" /> {t("onboarding.register.submitBasic")}</>
                ) : (
                  <><Mountain size={15} className="mr-2 shrink-0" /> {t("onboarding.register.submit")}</>
                )}
              </Button>

              <p className="mt-3 text-[11px] text-slate-500 leading-relaxed text-center">
                {t("onboarding.register.termsPrefix")}
                {" "}<Link to="/terms" target="_blank" rel="noopener noreferrer" data-testid="onboarding-terms-link" className="underline hover:text-slate-300">{t("onboarding.register.terms")}</Link>
                {" "}{t("onboarding.register.and")} <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="onboarding-privacy-link" className="underline hover:text-slate-300">{t("onboarding.register.privacy")}</Link>.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
